import { useState, useEffect } from 'react';
import axios from '../../../services/api';
import { FaMapMarkerAlt, FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';
import MapModal from './MapModal';
import { generateUUID } from '../../../services/utils';
import { useTranslation } from 'react-i18next';

export default function CreateEntranceModal({ isOpen, onClose, onSuccess, entrance=null, community=null }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    entranceId: generateUUID(),
    entranceName: '',
    entranceAddress: community?.communityAddress,
    longitude: community?.longitude,
    latitude: community?.latitude,
    serviceRange: '',
    contacts: '',
    contactsPhone: '',
    email: '',
    communityId: community ? community._id : ''
  });
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);

  const handleLocationSelect = ({ latitude, longitude, name }) => {
    setFormData(prev => ({
      ...prev,
      latitude: latitude.toFixed(6),
      longitude: longitude.toFixed(6),
      entranceAddress: name
    }));
  };

  useEffect(() => {
    if (entrance) {
      setFormData({
        entranceId: entrance.entranceId || '',
        entranceName: entrance.entranceName || '',
        entranceAddress: entrance.entranceAddress || '',
        longitude: entrance.longitude || '',
        latitude: entrance.latitude || '',
        serviceRange: entrance.serviceRange || '',
        contacts: entrance.contacts || '',
        contactsPhone: entrance.contactsPhone || '',
        email: entrance.email || '',
      });
    } else {
      setFormData({
        entranceId: generateUUID(),
        entranceName: '',
        entranceAddress: community?.communityAddress,
        longitude: community?.longitude,
        latitude: community?.latitude,
        serviceRange: '',
        contacts: '',
        contactsPhone: '',
        email: '',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entrance]);
    // eslint-disable-next-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!formData.entranceName || !formData.entranceAddress || !formData.longitude || !formData.latitude) {
      toast.error(t('Please fill in all required fields'));
      return;
    }

    try {
      if (entrance) {
        await axios.put(`/entrances/${entrance._id}`, formData);
        toast.success(t('Entrance updated successfully'));
      } else {
        await axios.post('/entrances', { ...formData, communityId: community._id });
        toast.success(t('Entrance created successfully'));
      }
      setFormData({
        entranceId: generateUUID(),
        entranceName: '',
        entranceAddress: '',
        longitude: '',
        latitude: '',
        serviceRange: '',
        contacts: '',
        contactsPhone: '',
        email: '',
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      toast.error(t('Entrance save error'));
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/50 flex items-center justify-end pr-[15%] pt-[7%] z-[60] app-text">
        <div className="bg-white w-[40vw] h-[55vh] rounded-lg shadow-xl relative">
          <div className="bg-black text-white px-4 py-3 rounded-t-lg flex justify-between items-center">
            <h2 className="text-lg font-semibold">
              {entrance ? t('Edit Entrance') : t('Create New Entrance')}
            </h2>
            <button onClick={onClose}>
              <FaTimes />
            </button>
          </div>

          <div className="p-4 h-[calc(55vh-116px)] overflow-y-auto">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1">{t('Entrance ID')}</label>
                <input
                  type="text"
                  value={formData.entranceId}
                  onChange={(e) => setFormData({ ...formData, entranceId: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('Entrance Name')}</label>
                <input
                  type="text"
                  value={formData.entranceName}
                  onChange={(e) => setFormData({ ...formData, entranceName: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('Entrance Address')}</label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.entranceAddress}
                    onChange={(e) => setFormData({ ...formData, entranceAddress: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                    required
                  />
                  <button
                    onClick={() => setIsMapModalOpen(true)}
                    className="px-3 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                    title="Find on map"
                  >
                    <FaMapMarkerAlt className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('Longitude')}</label>
                <input
                  type="text"
                  value={formData.longitude}
                  onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('Latitude')}</label>
                <input
                  type="text"
                  value={formData.latitude}
                  onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">{t('Service Range (Meters)')}</label>
                <input
                  type="number"
                  value={formData.serviceRange}
                  onChange={(e) => setFormData({ ...formData, serviceRange: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2"
                />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">{t('Contacts')}</label>
                  <input
                    type="text"
                    value={formData.contacts}
                    onChange={(e) => setFormData({ ...formData, contacts: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('Contacts Phone')}</label>
                  <input
                    type="text"
                    value={formData.contactsPhone}
                    onChange={(e) => setFormData({ ...formData, contactsPhone: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">{t('Email')}</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-0 left-0 right-0 px-4 py-3 border-t bg-white">
            <div className="flex justify-end gap-2">
              <button
                onClick={onClose}
                className="px-4 py-1.5 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
              >
                {t('Cancel')}
              </button>
              <button
                onClick={handleSubmit}
                className="px-4 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                {entrance ? t('Update') : t('Save')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <MapModal
        isOpen={isMapModalOpen}
        onClose={() => setIsMapModalOpen(false)}
        address2={formData.entranceAddress}
        onSelectLocation={handleLocationSelect}
      />
    </>
  );
}
